import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

// Identity roles that determine homepage routing (not modifier roles)
// Note: Backend normalizes "creator" → "author", so we need to handle both
const IDENTITY_ROLES = ['reader', 'creator', 'author', 'publisher', 'admin'];

// Role priority for determining default active role (highest to lowest)
// Note: "author" and "creator" are equivalent - "author" is canonical
const ROLE_PRIORITY = ['admin', 'publisher', 'author', 'creator', 'reader'];

// Normalize role - treat "author" as "creator" for frontend consistency
// Frontend uses "creator" internally, backend uses "author"
const normalizeRole = (role) => {
  if (role === 'author') return 'creator';
  return role;
};

// Get the highest priority identity role from a roles array
const getHighestPriorityRole = (roles) => {
  const normalizedRoles = (roles || []).map(normalizeRole);
  const identityRoles = normalizedRoles.filter(r => IDENTITY_ROLES.includes(r));
  for (const role of ROLE_PRIORITY) {
    const checkRole = normalizeRole(role);
    if (identityRoles.includes(checkRole)) {
      return checkRole;
    }
  }
  return 'reader'; // Default fallback
};

// Get all identity roles from a roles array (normalized to frontend values)
const getIdentityRoles = (roles) => {
  const normalizedRoles = (roles || []).map(normalizeRole);
  // Filter to identity roles, but dedupe (author and creator are same)
  const filtered = normalizedRoles.filter(r => ['reader', 'creator', 'publisher', 'admin'].includes(r));
  return [...new Set(filtered)]; // Remove duplicates
};

// Route mapping for each role
const ROLE_ROUTES = {
  reader: '/home',
  creator: '/author',
  author: '/author',  // Backend sends "author", route to same place
  publisher: '/publisher',
  admin: '/admin'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [backendAvailable, setBackendAvailable] = useState(true);
  
  // Multi-role state
  const [activeRole, setActiveRoleState] = useState(null);
  const [showRoleSelectionModal, setShowRoleSelectionModal] = useState(false);
  const [pendingRoleSelection, setPendingRoleSelection] = useState(false);

  // Set active role with sessionStorage persistence
  const setActiveRole = (role) => {
    setActiveRoleState(role);
    if (role) {
      sessionStorage.setItem('activeRole', role);
      // Dispatch event so ReaderCreditContext can refresh when switching to reader role
      window.dispatchEvent(new CustomEvent('role-switch', { detail: { role } }));
    } else {
      sessionStorage.removeItem('activeRole');
    }
  };

  // Check if user needs to select a role (has 2+ identity roles)
  const needsRoleSelection = (userData) => {
    const identityRoles = getIdentityRoles(userData?.roles);
    return identityRoles.length >= 2;
  };

  // Get the route for a given role
  const getRoleRoute = (role) => {
    return ROLE_ROUTES[role] || '/home';
  };

  // Load current user from token
  const loadCurrentUser = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return { success: false, shouldRetry: false };
    }

    // Check for persisted active role
    const persistedActiveRole = sessionStorage.getItem('activeRole');

    try {
      console.log('[Auth] Loading current user...');
      const headers = {
        Authorization: `Bearer ${token}`
      };
      
      // Include X-Active-Role header if we have a persisted role
      if (persistedActiveRole) {
        headers['X-Active-Role'] = persistedActiveRole;
      }
      
      const response = await api.get('/auth/me', {
        headers,
        timeout: 2000  // 2 second timeout (reduced from 5)
      });
      
      console.log('[Auth] User loaded successfully');
      const userData = response.data;
      setUser(userData);
      setIsAuthenticated(true);
      setAccessToken(token);
      
      // Resolve active role
      const identityRoles = getIdentityRoles(userData.roles);
      
      if (persistedActiveRole && identityRoles.includes(persistedActiveRole)) {
        // Restore persisted role if valid
        setActiveRoleState(persistedActiveRole);
      } else if (identityRoles.length === 1) {
        // Single identity role - auto-set
        setActiveRole(identityRoles[0]);
      } else if (identityRoles.length >= 2) {
        // Multiple identity roles - need selection
        const highestRole = getHighestPriorityRole(userData.roles);
        setActiveRoleState(highestRole); // Temporary until modal selection
        setPendingRoleSelection(true);
      } else {
        // No identity roles (shouldn't happen) - default to reader
        setActiveRole('reader');
      }
      
      return { success: true, shouldRetry: false };
    } catch (error) {
      console.error('[Auth] Failed to load user:', error.message);
      
      // Get HTTP status code if available
      const httpStatus = error.response?.status;
      
      // CRITICAL: Handle 5xx errors - backend instability, NOT auth failure
      // DO NOT clear token or logout on 5xx errors
      if (httpStatus >= 500 && httpStatus < 600) {
        console.warn(`[Auth] ${httpStatus} Server Error - keeping token for retry (backend instability)`);
        // Keep the token but mark as not authenticated for now
        setIsAuthenticated(false);
        setUser(null);
        return { success: false, shouldRetry: true };
      }
      
      // Check if it's a timeout or network error (backend might be restarting)
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        console.warn('[Auth] Backend unavailable - keeping token for retry');
        // Keep the token but mark as not authenticated for now
        setIsAuthenticated(false);
        setUser(null);
        return { success: false, shouldRetry: true };
      }
      
      // Only clear token on 401 (unauthorized) or 403 (forbidden)
      if (httpStatus === 401 || httpStatus === 403) {
        // Check if it's a banned user
        const errorDetail = error.response?.data?.detail;
        if (errorDetail?.error === 'account_suspended') {
          console.log('[Auth] Account suspended - clearing token');
        } else {
          console.log('[Auth] Auth error - clearing invalid token');
        }
        localStorage.removeItem('accessToken');
        setAccessToken(null);
        setUser(null);
        setIsAuthenticated(false);
        return { success: false, shouldRetry: false };
      }
      
      // For other errors (400, 404, etc.), don't clear token but don't retry either
      console.log('[Auth] Non-auth error - keeping token');
      setIsAuthenticated(false);
      setUser(null);
      return { success: false, shouldRetry: false };
    } finally {
      setLoading(false);
    }
  };

  // Login with token
  const loginWithToken = async (token) => {
    localStorage.setItem('accessToken', token);
    setAccessToken(token);
    
    try {
      const response = await api.get('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setUser(response.data);
      setIsAuthenticated(true);
      
      // Dispatch auth-change event for other components (e.g., ReaderCreditContext)
      window.dispatchEvent(new CustomEvent('auth-change', { detail: { type: 'login', user: response.data } }));
      
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user after login:', error);
      throw error;
    }
  };

  // Logout with backend cleanup
  const logout = async () => {
    const token = localStorage.getItem('accessToken');
    
    // Call backend logout endpoint for cleanup (fire and forget)
    if (token) {
      try {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        });
        console.log('[Auth] Logout cleanup completed');
      } catch (error) {
        // Don't block logout if cleanup fails
        console.warn('[Auth] Logout cleanup warning:', error.message);
      }
    }
    
    // Clear local state regardless of backend response
    localStorage.removeItem('accessToken');
    sessionStorage.removeItem('activeRole');
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setActiveRoleState(null);
    setPendingRoleSelection(false);
    setShowRoleSelectionModal(false);
  };

  // Get the appropriate homepage based on user roles
  // Priority: Admin (highest) > Publisher > Author > Reader (lowest)
  // Note: "creator" is legacy, "author" is canonical
  const getHomePage = (userData, selectedRole = null) => {
    // If a specific role is selected, use its route
    if (selectedRole && ROLE_ROUTES[selectedRole]) {
      return ROLE_ROUTES[selectedRole];
    }
    
    const roles = userData?.roles || [];
    
    // Helper: check for author role (handles legacy "creator")
    const hasAuthorRole = roles.includes('author') || roles.includes('creator');
    const hasPublisherRole = roles.includes('publisher');
    
    // Highest role takes precedence
    if (roles.includes('admin')) {
      return '/admin';
    } else if (hasPublisherRole) {
      return '/publisher'; // Publisher dashboard
    } else if (hasAuthorRole) {
      return '/author'; // Canonical route
    } else {
      return '/home'; // Default reader homepage
    }
  };

  // Handle role selection from modal
  const handleRoleSelection = (selectedRole) => {
    setActiveRole(selectedRole);
    setShowRoleSelectionModal(false);
    setPendingRoleSelection(false);
    return getRoleRoute(selectedRole);
  };

  // Trigger role selection modal for multi-role users after login
  const triggerRoleSelectionIfNeeded = (userData) => {
    const identityRoles = getIdentityRoles(userData?.roles);
    if (identityRoles.length >= 2) {
      setShowRoleSelectionModal(true);
      return true; // Needs selection
    }
    return false; // Auto-routed
  };

  // Load user on mount with retry logic and maximum timeout
  useEffect(() => {
    let isMounted = true;
    let retryTimeout = null;
    
    // Set a maximum loading timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (isMounted) {
        console.warn('[Auth] Loading timeout - forcing loading state to false');
        setLoading(false);
        setBackendAvailable(false);
      }
    }, 3000); // 3 seconds max (reduced from 10)
    
    const attemptLoad = async () => {
      const result = await loadCurrentUser();
      if (!isMounted) return;
      
      clearTimeout(loadingTimeout); // Cancel timeout if load succeeds
      
      // Only retry if backend was unavailable (not if token was invalid or user loaded successfully)
      if (result.shouldRetry) {
        console.log('[Auth] Backend may be starting - will retry in 2 seconds');
        retryTimeout = setTimeout(() => {
          if (isMounted) {
            console.log('[Auth] Retrying user load...');
            loadCurrentUser();
          }
        }, 2000); // Reduced from 3 seconds
      }
    };
    
    attemptLoad();
    
    return () => {
      isMounted = false;
      clearTimeout(loadingTimeout);
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, []); // Only run on mount

  const value = {
    user,
    accessToken,
    isAuthenticated,
    loading,
    backendAvailable,
    loginWithToken,
    logout,
    loadCurrentUser,
    getHomePage,
    // Multi-role support
    activeRole,
    setActiveRole,
    showRoleSelectionModal,
    setShowRoleSelectionModal,
    pendingRoleSelection,
    setPendingRoleSelection,
    needsRoleSelection,
    handleRoleSelection,
    triggerRoleSelectionIfNeeded,
    getIdentityRoles,
    getRoleRoute
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
